/**
 * What a GPX file contains, counted the way the SERVER counts it. These numbers
 * are shown to the user before an import is accepted, so they have to describe
 * what will actually be saved:
 *
 *  - geometry comes from the FIRST `<trk>` including ALL of its `<trkseg>`s
 *    (`GpxGeometryBuilder.BuildGeometryFromGpx`), so segments are never counted
 *    — a single multi-segment track discards nothing;
 *  - `<rte>` is only a fallback: when the file has any `<trk>`, every `<rte>`
 *    is ignored;
 *  - waypoints are the direct `<wpt>` children of `<gpx>`
 *    (`GpxSerializer.ParseWaypoints`), and all of them are imported and
 *    attached to the one route that gets created.
 */
export interface IGpxContentSummary {
    /** Number of `<trk>` elements. */
    trackCount: number;
    /** Number of `<rte>` elements. */
    routeCount: number;
    /** Number of `<wpt>` elements directly under `<gpx>`. */
    waypointCount: number;
    /** Line features the server will drop, because one file becomes one route. */
    discardedCount: number;
}

const EMPTY_SUMMARY: IGpxContentSummary = {
    trackCount: 0,
    routeCount: 0,
    waypointCount: 0,
    discardedCount: 0
};

/** Counts the tracks, routes and waypoints in a GPX document. */
export function summariseGpxContents(gpxXml: string): IGpxContentSummary {
    const root = parseGpxRoot(gpxXml);
    if (!root) {
        return EMPTY_SUMMARY;
    }

    //match on local name: an 'application/xml' document matches getElementsByTagName
    //on the QUALIFIED name, so a prefixed file (<gpx:trk>) would count zero tracks
    const trackCount = root.getElementsByTagNameNS('*', 'trk').length;
    const routeCount = root.getElementsByTagNameNS('*', 'rte').length;
    const waypointCount = countChildElements(root, 'wpt');

    return {
        trackCount,
        routeCount,
        waypointCount,
        discardedCount: trackCount > 0
            ? (trackCount - 1) + routeCount
            : Math.max(0, routeCount - 1)
    };
}

/**
 * A warning describing what an import will throw away, or '' when the file maps
 * cleanly onto a single route.
 */
export function describeDiscardedContent(summary: IGpxContentSummary): string {
    if (summary.discardedCount < 1) {
        return '';
    }

    const contents: string[] = [];
    if (summary.trackCount > 0) {
        contents.push(pluralise(summary.trackCount, 'track'));
    }
    if (summary.routeCount > 0) {
        contents.push(pluralise(summary.routeCount, 'route'));
    }

    const keptKind = summary.trackCount > 0 ? 'track' : 'route';
    const ignored = summary.discardedCount === 1
        ? 'the other one'
        : `the other ${summary.discardedCount}`;

    const waypoints = summary.waypointCount > 0
        ? ' but all waypoints will be imported'
        : '';

    return `This file contains ${contents.join(' and ')}. `
        + `Only the first ${keptKind} will be imported, ${ignored} will be ignored${waypoints}.`;
}

/** Parses GPX text and returns its root element, or null if it isn't usable XML. */
function parseGpxRoot(gpxXml: string): Element | null {
    if (!gpxXml) {
        return null;
    }
    try {
        const document = new DOMParser().parseFromString(gpxXml, 'application/xml');
        if (document.getElementsByTagName('parsererror').length > 0) {
            return null;
        }
        return document.documentElement ?? null;
    } catch (error) {
        console.error('Failed to parse GPX file contents', error);
        return null;
    }
}

/** Counts direct child elements with the given local name (namespace-agnostic). */
function countChildElements(parent: Element, localName: string): number {
    let count = 0;
    for (let index = 0; index < parent.children.length; index++) {
        if (parent.children[index].localName === localName) {
            count++;
        }
    }
    return count;
}

function pluralise(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
